import { useState, useEffect, useRef } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { AuthLogo } from './AuthLogo';
import { authControlStyles as controls } from './AuthControls';
import styles from './VerifyAccountCard.module.css';

/**
 * VerifyAccountCard — email verification pending card, in the sign-up form column.
 *
 * Mirrors Flutter VerifyAccCard (presentation/web/auth/web_signup/widgets/verify_acc_card.dart):
 * the auth logo, then a white card (radius 20, #EDECEC border) with "Verify Account", the
 * registered email, "Didn't receive the verification email?" with the 1-minute countdown, and
 * the AppButtons Resend Verification Email, Re-sign up, "or", Log out (outlined).
 *
 * Flutter source files:
 *   - presentation/web/auth/web_signup/widgets/verify_acc_card.dart
 *   - domain/core/constants/string_constants.dart (VerifyAccCardConstants)
 */

interface VerifyAccountCardProps {
  email: string;
  disableResendBtn: boolean;
  onResend: () => void;
  onReSignUp: () => void;
  onLogout: () => void;
  onTimerEnd: () => void;
  isLoading?: boolean;
}

/** Flutter's countdown text: '0$minutes:$seconds', seconds not zero-padded ("00:9") */
function formatTime(seconds: number): string {
  return `0${Math.floor(seconds / 60)}:${seconds % 60}`;
}

export function VerifyAccountCard({
  email,
  disableResendBtn,
  onResend,
  onReSignUp,
  onLogout,
  onTimerEnd,
  isLoading = false,
}: VerifyAccountCardProps) {
  const { t } = useLocalization();
  const [secondsRemaining, setSecondsRemaining] = useState(60);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Start countdown timer
  useEffect(() => {
    if (disableResendBtn) {
      setSecondsRemaining(60);
      timerRef.current = setInterval(() => {
        setSecondsRemaining((prev) => {
          if (prev <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            onTimerEnd();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [disableResendBtn, onTimerEnd]);

  return (
    <div className={styles.wrapper}>
      <AuthLogo className={styles.logo} />

      <section className={styles.card} aria-labelledby="verify-account-title">
        <h1 id="verify-account-title" className={styles.title}>
          {t('Verify Account')}
        </h1>

        <p className={styles.description}>
          {t('We have sent an account activation link to your Email ID ')}
          <strong>{`${email}.`}</strong>
          {t(' Please click on the link to activate your account.')}
        </p>

        <div className={styles.resendRow}>
          <span className={styles.resendLabel}>
            {t("Didn't receive the verification email?")}
          </span>
          {disableResendBtn && secondsRemaining > 0 && (
            <span className={styles.timer}>{formatTime(secondsRemaining)}</span>
          )}
        </div>

        <button
          type="button"
          className={controls.appButton}
          onClick={onResend}
          disabled={disableResendBtn || isLoading}
        >
          {t('Resend Verification Email')}
        </button>

        <button
          type="button"
          className={`${controls.appButton} ${styles.reSignUpBtn}`}
          onClick={onReSignUp}
          disabled={isLoading}
        >
          {t('Re-sign up')}
        </button>

        <div className={styles.divider}>
          <span className={styles.dividerLine} />
          <span className={styles.dividerText}>{t('or')}</span>
          <span className={styles.dividerLine} />
        </div>

        <button
          type="button"
          className={`${controls.appButton} ${controls.appButtonOutlined}`}
          onClick={onLogout}
          disabled={isLoading}
        >
          {t('Log out')}
        </button>
      </section>
    </div>
  );
}
